import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

/**
 * Reusable inner-page hero. Smaller than the home hero — cinematic but
 * proportioned for content pages. Includes parallax + grain + breadcrumb.
 */
export default function PageHero({
  eyebrow,
  title,
  subtitle,
  image,
  imageAlt,
  breadcrumb = [],
  imagePosition = "center 35%",
}) {
  const bgRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = window.scrollY;
      bgRef.current.style.transform = `translate3d(0, ${y * 0.15}px, 0) scale(1.06)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      data-testid="page-hero"
      className="relative overflow-hidden grain bg-[var(--crest-charcoal)] pt-32 md:pt-40 pb-20 md:pb-28"
    >
      <div
        ref={bgRef}
        role={imageAlt ? "img" : undefined}
        aria-label={imageAlt}
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: `url('${image}')`,
          backgroundSize: "cover",
          backgroundPosition: imagePosition,
          transform: "scale(1.06)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[rgba(18,18,18,0.7)] via-[rgba(18,18,18,0.55)] to-[rgba(18,18,18,0.95)]" />
      <div className="absolute inset-0 bg-gradient-to-tr from-[rgba(217,119,6,0.10)] via-transparent to-[rgba(30,41,59,0.4)]" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {breadcrumb.length > 0 && (
          <nav
            data-testid="page-hero-breadcrumb"
            className="reveal flex items-center gap-2 mb-8 font-display uppercase tracking-[0.3em] text-[10px] font-semibold text-white/50"
          >
            {breadcrumb.map((b, i) => (
              <span key={b.label} className="flex items-center gap-2">
                {b.href ? (
                  <Link
                    to={b.href}
                    className="hover:text-[var(--crest-amber)] transition-colors"
                  >
                    {b.label}
                  </Link>
                ) : (
                  <span className="text-white/80">{b.label}</span>
                )}
                {i < breadcrumb.length - 1 && (
                  <ChevronRight size={12} className="text-white/30" />
                )}
              </span>
            ))}
          </nav>
        )}

        {eyebrow && (
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[var(--crest-amber)]" />
            <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
              {eyebrow}
            </span>
          </div>
        )}

        <h1
          data-testid="page-hero-title"
          className="reveal font-display uppercase font-extrabold leading-[0.95] tracking-tight text-white max-w-4xl"
          style={{ fontSize: "clamp(2.4rem, 6.5vw, 5.5rem)" }}
        >
          {title}
        </h1>

        {subtitle && (
          <p
            data-testid="page-hero-subtitle"
            className="reveal font-body text-base md:text-lg text-white/75 mt-6 max-w-2xl leading-relaxed"
            style={{ transitionDelay: "150ms" }}
          >
            {subtitle}
          </p>
        )}
      </div>
    </section>
  );
}
